/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { BlockSnapshot } from "@vienna/common/src";

import { MathUtils } from "../../../utilities/MathUtils";
import { StyleConstants } from "../../../utilities/StyleConstants";
import { ResizeCommand } from "../../Command";
import { Plate } from "../../Plate";
import { BaseSession } from "../BaseSession";
import { Key } from "src/classes/plate/Keyboard";
import { ResizeSessionSnapshot } from "src/classes/plate/Session";
import { SessionType } from "src/classes/plate/Session/session.types";

export class ResizeSession extends BaseSession<ResizeSessionSnapshot> {
  readonly initialBlockSnapshots: BlockSnapshot[];

  plate: Plate;

  private constructor(snapshot: ResizeSessionSnapshot, plate: Plate) {
    super(snapshot);
    this.plate = plate;
    this.initialBlockSnapshots = this.plate.selectedBlocks.map((block) => block.snapshot);
    this.onResize = this.onResize.bind(this);
  }

  start(): void {
    document.addEventListener("mousemove", this.onResize);
  }

  cancel(): void {
    this.end();
  }

  end(): void {
    document.removeEventListener("mousemove", this.onResize);

    const cmd = new ResizeCommand(this, this.plate);

    if (cmd.blocksResized()) {
      this.plate.addToHistory(cmd);
    }
  }

  onResize(): void {
    this.plate.selectedBlocks.forEach((block) => {
      const initialBlockSnapshot = this.initialBlockSnapshots.find(({ id }) => id === block.snapshot.id);
      if (!initialBlockSnapshot) throw new Error("Can't resize because can't find block");
      const isPreservingAR = this.plate.keyboardManager.isKeyPressed(Key.Shift);
      const aspectRatio = isPreservingAR ? initialBlockSnapshot.width / initialBlockSnapshot.height : null;

      const mousePosition = this.plate.mouseTracker.snapshot.position;
      const { dx, dy } = MathUtils.getDistanceBetweenPoints(
        mousePosition,
        this.snapshot.anchor,
        false,
        aspectRatio
      );

      const naiveWidth = initialBlockSnapshot.width + dx;
      const maxWidth = this.plate.snapshot.width - initialBlockSnapshot.left;
      const width = MathUtils.constrainValueToBounds(naiveWidth, StyleConstants.minBlockWidth, maxWidth);

      const naiveHeight = initialBlockSnapshot.height + dy;
      const maxHeight = this.plate.snapshot.height - initialBlockSnapshot.top;
      const height = MathUtils.constrainValueToBounds(naiveHeight, StyleConstants.minBlockHeight, maxHeight);

      block.resize(width, height);
    });
  }

  /* -------------------------------------------------- */
  /*                  Static Methods                    */
  /* -------------------------------------------------- */
  public static create(plate: Plate): ResizeSession {
    const initialSnapshot: ResizeSessionSnapshot = {
      type: SessionType.Resize,
      anchor: { x: plate.mouseTracker.snapshot.position.x, y: plate.mouseTracker.snapshot.position.y },
    };
    return new ResizeSession(initialSnapshot, plate);
  }
}
